import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { ISO_COUNTRY_NAMES } from '../utils/countryNames.js'
import { localizeSalesperson } from '../utils/localizedField.js'

const US_STATE_NAMES = {
  AK: 'Alaska', AL: 'Alabama', AR: 'Arkansas', AZ: 'Arizona',
  CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DC: 'District of Columbia', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', IA: 'Iowa', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', KS: 'Kansas', KY: 'Kentucky',
  LA: 'Louisiana', MA: 'Massachusetts', MD: 'Maryland', ME: 'Maine',
  MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri', MS: 'Mississippi',
  MT: 'Montana', NC: 'North Carolina', ND: 'North Dakota', NE: 'Nebraska',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NV: 'Nevada',
  NY: 'New York', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VA: 'Virginia', VT: 'Vermont', WA: 'Washington', WI: 'Wisconsin',
  WV: 'West Virginia', WY: 'Wyoming',
}

const CA_PROVINCE_NAMES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland and Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario',
  PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
}

export default ({ appId, searchKey, indexEnv, brandSlug, locale }) => ({
  loading: true,
  error: false,
  country: '',
  stateProvince: '',
  people: [],
  countries: [],

  get peopleInCountry() {
    return this.people.filter(p => p.country_iso_codes?.includes(this.country))
  },

  get hasFilteredLocations() {
    if (this.country === 'US') {
      return this.peopleInCountry.some(p => p.state_codes?.length > 0)
    }
    if (this.country === 'CA') {
      return this.peopleInCountry.some(p => p.province_codes?.length > 0)
    }
    return false
  },

  get filteredLocations() {
    if (this.country === 'US') {
      const codes = [...new Set(this.peopleInCountry.flatMap(p => p.state_codes ?? []))]
      return codes
        .filter(code => US_STATE_NAMES[code])
        .sort((a, b) => US_STATE_NAMES[a].localeCompare(US_STATE_NAMES[b]))
        .map(code => ({ value: code, label: US_STATE_NAMES[code] }))
    }
    if (this.country === 'CA') {
      const codes = [...new Set(this.peopleInCountry.flatMap(p => p.province_codes ?? []))]
      return codes
        .filter(code => CA_PROVINCE_NAMES[code])
        .sort((a, b) => CA_PROVINCE_NAMES[a].localeCompare(CA_PROVINCE_NAMES[b]))
        .map(code => ({ value: code, label: CA_PROVINCE_NAMES[code] }))
    }
    return []
  },

  get filteredPeople() {
    return this.people.filter((person) => {
      const matchesCountry = !this.country || person.country_iso_codes?.includes(this.country)
      let matchesRegion = true
      if (this.stateProvince && this.country === 'US') {
        matchesRegion = person.state_codes?.includes(this.stateProvince) ?? false
      } else if (this.stateProvince && this.country === 'CA') {
        matchesRegion = person.province_codes?.includes(this.stateProvince) ?? false
      }
      return matchesCountry && matchesRegion
    })
  },

  async init() {
    const client = algoliasearch(appId, searchKey)
    try {
      const response = await client.search({
        requests: [{
          indexName: `${indexEnv}_salespeople`,
          query: '',
          filters: `brand_slugs:"${brandSlug}" AND record_type:salesperson`,
          hitsPerPage: 500,
          attributesToRetrieve: [
            'objectID', 'full_name', 'job_title', 'job_title_translations',
            'market_product_focus', 'market_product_focus_translations', 'email',
            'work_phone', 'mobile_phone', 'photo_url', 'territory_names',
            'country_iso_codes', 'state_codes', 'province_codes',
          ],
        }],
      })
      this.people = response.results[0].hits.map(person => localizeSalesperson(person, locale))

      const countryCodes = [...new Set(this.people.flatMap(p => p.country_iso_codes ?? []))]
      this.countries = countryCodes
        .filter(code => ISO_COUNTRY_NAMES[code])
        .sort((a, b) => ISO_COUNTRY_NAMES[a].localeCompare(ISO_COUNTRY_NAMES[b]))
        .map(code => ({ value: code, label: ISO_COUNTRY_NAMES[code] }))
    } catch {
      this.error = true
    } finally {
      this.loading = false
    }

    const announce = () => {
      const count = this.filteredPeople.length
      this.$refs.liveRegion.textContent = count
        ? `Showing ${count} sales representative${count === 1 ? '' : 's'}`
        : 'No sales representatives found.'
    }
    this.$watch('country', announce)
    this.$watch('stateProvince', announce)
  },
})
